import {
  Box,
  Divider,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useTranslation } from "react-i18next";
import { useGetTruckCostFormulaQuery } from "src/store/api/generatedApi";
import type {
  MillInfo,
  RatesToMillsResponse,
  TruckRoute,
} from "src/store/api/generatedApi";

interface RatesTableProps {
  ratesData: RatesToMillsResponse;
  truckRoutes: Record<string, TruckRoute>;
  mills: MillInfo[];
}

export const RatesTable = ({
  ratesData,
  truckRoutes,
  mills,
}: RatesTableProps) => {
  const { t } = useTranslation();
  const { data: costFormula } = useGetTruckCostFormulaQuery();

  const formatRate = (rate: number | null | undefined) => {
    if (rate == null) return t("logisticsNotAvailable");
    return `$${rate.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  return (
    <Box>
      <Divider sx={{ my: 1 }} />
      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
        {t("logisticsRatesFrom", {
          station: ratesData.origin_station,
          state: ratesData.origin_state,
        })}
      </Typography>
      <Table size="medium">
        <TableHead>
          <TableRow>
            <TableCell>{t("logisticsDestination")}</TableCell>
            <TableCell align="right">{t("logisticsPrivateCar")}</TableCell>
            <TableCell align="right">{t("logisticsRailroadCar")}</TableCell>
            <TableCell align="right">
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="flex-end"
                spacing={0.5}
              >
                {t("logisticsTruckEstimated")}
                <Tooltip
                  title={
                    costFormula
                      ? t("logisticsTruckCostFormula", {
                          base: `$${costFormula.base_cost_usd}`,
                          perMile: `$${costFormula.cost_per_mile_usd}`,
                        })
                      : ""
                  }
                  arrow
                >
                  <InfoOutlinedIcon
                    sx={{ fontSize: 16, color: "text.secondary" }}
                  />
                </Tooltip>
              </Stack>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {ratesData.rates.map((rate) => {
            const mill = mills.find((m) => m.tenant_name === rate.tenant_name);
            const truck = truckRoutes[rate.tenant_name];

            return (
              <TableRow key={rate.tenant_name}>
                <TableCell>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Box
                      sx={{
                        width: 14,
                        height: 14,
                        borderRadius: "50%",
                        backgroundColor: mill?.color ?? "#888",
                      }}
                    />
                    <Typography variant="body1" fontWeight={500}>
                      {mill?.display_name ?? rate.destination_name}
                    </Typography>
                  </Stack>
                </TableCell>
                <TableCell align="right">
                  <Typography
                    variant="h6"
                    fontWeight={600}
                    color={
                      rate.rate_per_ton_private_car_usd
                        ? "text.primary"
                        : "text.disabled"
                    }
                  >
                    {formatRate(rate.rate_per_ton_private_car_usd)}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography
                    variant="h6"
                    fontWeight={600}
                    color={
                      rate.rate_per_ton_railroad_car_usd
                        ? "text.primary"
                        : "text.disabled"
                    }
                  >
                    {formatRate(rate.rate_per_ton_railroad_car_usd)}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography
                    variant="h6"
                    fontWeight={600}
                    color={
                      truck?.cost_per_ton_usd ? "text.primary" : "text.disabled"
                    }
                  >
                    {formatRate(truck?.cost_per_ton_usd ?? null)}
                  </Typography>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Box>
  );
};
